import { AttributeSettingsDTO } from 'src/app/models/product-models/DTOs/AttributeSettingsDTO';
import { CoveringDTO } from 'src/app/models/product-models/DTOs/CoveringDTO';
import { FloorplanDTO } from 'src/app/models/product-models/DTOs/FloorplanDTO';
import { ProductDTO } from 'src/app/models/product-models/DTOs/ProductDTO';
import { TextureDataDTO } from 'src/app/models/product-models/DTOs/TextureDataDTO';
import { ProductBlueprint } from 'src/app/models/product-models/views/ProductBlueprint';
import { Utility } from 'src/app/models/product-models/views/Utility';
import { Euler, Quaternion, Vector2, Vector3 } from 'three';
import { Corner } from './corner';
import { Room } from './room';
import { Fn, Utils } from './utils';
import { Wall } from './wall';
import { WallObject } from './wall_object';
import { WallDTO } from 'src/app/models/product-models/DTOs/WallDto';

export class Floorplan {
  private _defaultFloorPlanTolerance = 30.0;
  /** */
  private _walls: Wall[] = [];

  /** */
  private _rooms: Room[] = [];

  /** */
  private _corners: Corner[] = [];

  /** */
  private _newWallCallbacks: Fn[] = [];

  /** */
  private _newCornerCallbacks: Fn[] = [];

  /** */
  private _redrawCallbacks: Fn[] = [];

  /** */
  private _updatedRooms: Fn[] = [];

  /** */
  public roomLoadedCallbacks: Fn[] = [];

  private _allCornersHeight = 250;

  /** Constructs a floorplan. */
  constructor() {
    if (window.innerWidth < 750) this._defaultFloorPlanTolerance = 50;
  }

  /*public wallEdges(): HalfEdge[] {
    const edges: HalfEdge[] = [];

    this._walls.forEach((wall) => {
      if (wall.frontEdge) {
        edges.push(wall.frontEdge);
      }
      if (wall.backEdge) {
        edges.push(wall.backEdge);
      }
    });

    return edges;
  }

  public wallEdgePlanes(): Mesh[] {
    const planes: Mesh[] = [];

    this._walls.forEach((wall) => {
      if (wall.frontEdge && wall.frontEdge.plane) {
        planes.push(wall.frontEdge.plane);
      }
      if (wall.backEdge && wall.backEdge.plane) {
        planes.push(wall.backEdge.plane);
      }
    });

    return planes;
  }

  private floorPlanes(): Mesh[] {
    return Utils.map(this._rooms, (room: Room) => {
      return room.floorPlane;
    });
  }*/

  public fireOnNewWall(callback: Fn) {
    this._newWallCallbacks.push(callback);
  }

  public fireOnNewCorner(callback: Fn) {
    this._newCornerCallbacks.push(callback);
  }

  public fireOnRedraw(callback: Fn) {
    this._redrawCallbacks.push(callback);
  }

  public fireOnUpdatedRooms(callback: Fn) {
    this._updatedRooms.push(callback);
  }

  public fireRedraw() {
    this._redrawCallbacks.forEach((callback: Fn) => callback());
  }

  public setFloorTexture(texture: CoveringDTO) {
    this._rooms.forEach((room) => room.setFloorTexture(texture));
  }

  public setWallHeight(height: number) {
    this._walls.forEach((wall) => wall.setHeight(height));
  }

  public setAllCornersHeight(height: number) {
    this._allCornersHeight = height;
    this._corners.forEach((corner) => corner.setHeight(height));
  }

  public setCeilingTexture(texture: CoveringDTO) {
    this._rooms.forEach((room) => room.setCeilingTexture(texture));
  }

  public toDTO() {
    const corners: Corner[] = [];
    const walls: WallDTO[] = [];

    for (const wall of this._walls) {
      const wallData = wall.toDTO();
      walls.push(wallData.wallDTO);
      corners.push(...wallData.interiorCorners);
      corners.push(...wallData.exteriorCorners);
    }

    corners.forEach((corner) => {
      corner.setHeight(this.allCornersHeight);
    });

    return new FloorplanDTO(
      undefined,
      walls,
      this._rooms.map((room) => room.toDTO(corners)),
      corners.map((corner) => corner.toDTO()),
    );
  }

  public getMoveVector(): Vector2 {
    let maxX = -Infinity;
    let maxY = -Infinity;
    let minX = Infinity;
    let minY = Infinity;

    this._walls.forEach((wall: Wall) => {
      let [startX, startY, endX, endY] = [0, 0, 0, 0];

      if (wall.frontEdge)
        [startX, startY, endX, endY] = [
          wall.frontEdge.interiorStart().x,
          wall.frontEdge.interiorStart().y,
          wall.frontEdge.interiorEnd().x,
          wall.frontEdge.interiorEnd().y,
        ];

      if (wall.backEdge)
        [startX, startY, endX, endY] = [
          wall.backEdge.interiorStart().x,
          wall.backEdge.interiorStart().y,
          wall.backEdge.interiorEnd().x,
          wall.backEdge.interiorEnd().y,
        ];

      minX = Math.min(minX, startX, endX);
      minY = Math.min(minY, startY, endY);
      maxX = Math.max(maxX, startX, endX);
      maxY = Math.max(maxY, startY, endY);
    });

    return new Vector2((maxX + minX) / 2, (maxY + minY) / 2);
  }

  public getWallProducts(): ProductDTO[] {
    const products: ProductDTO[] = [];
    const moveVector = this.getMoveVector();

    for (const wall of this._walls) {
      for (const object of wall.getObjects()) {
        if (object.productBlueprint instanceof ProductBlueprint) {
          const product = ProductDTO.getEmptyProduct(Number(object.productBlueprint.id));

          product.wallId = wall.getId();

          const windowHeightOffset = object.heightOffset + (object.height * object.unitFactor) / 2;
          const productOffset = object.productBlueprint.movementData.productOffset;

          let [startX, startY, endX, endY] = [0, 0, 0, 0];

          if (wall.frontEdge) {
            [startX, startY, endX, endY] = [
              wall.frontEdge.interiorStart().x,
              wall.frontEdge.interiorStart().y,
              wall.frontEdge.interiorEnd().x,
              wall.frontEdge.interiorEnd().y,
            ];
          } else if (wall.backEdge) {
            [startX, startY, endX, endY] = [
              wall.backEdge.interiorStart().x,
              wall.backEdge.interiorStart().y,
              wall.backEdge.interiorEnd().x,
              wall.backEdge.interiorEnd().y,
            ];
          }

          const p1 = new Vector3((startX - moveVector.x) / 100, 0, (startY - moveVector.y) / 100);
          const p2 = new Vector3((endX - moveVector.x) / 100, 0, (endY - moveVector.y) / 100);
          const p3 = p2.clone();
          p3.y = wall.getHeight() / 100;

          const vector1 = new Vector3().subVectors(p2, p1);
          const vector2 = new Vector3().subVectors(p3, p1);
          const normal = new Vector3().crossVectors(vector1, vector2);

          normal.normalize();
          normal.negate();

          let xOffset = normal.x * (object.thickness + productOffset);
          let zOffset = normal.z * (object.thickness + productOffset);

          if (wall.frontEdge !== null) {
            xOffset = (normal.x * wall.getThickness()) / 100 + xOffset;
            zOffset = (normal.z * wall.getThickness()) / 100 + zOffset;
          }

          const xPosition = (object.x - moveVector.x) / 100 - xOffset;
          const yPosition = object.isWindowBuilder ? windowHeightOffset / 100 : 0;
          const zPosition = (object.y - moveVector.y) / 100 - zOffset;

          const position = new Vector3(xPosition, yPosition, zPosition);

          const quaternion = new Quaternion();
          quaternion.setFromUnitVectors(new Vector3(0, 0, -1), normal);

          const euler = new Euler().setFromQuaternion(quaternion, 'XZY');

          product.transformationData.position = position.toArray();
          product.transformationData.rotation = euler.toArray() as number[];

          const heightAttribute = object.productBlueprint.attributes.find((attribute) =>
            attribute.externalId.includes('HeightLimits'),
          );

          const widthAttribute = object.productBlueprint.attributes.find((attribute) =>
            attribute.externalId.includes('WidthLimits'),
          );

          if (heightAttribute) {
            const currentAttribute = product.attributeSettings.find(
              (attr) => attr.key.toString() === heightAttribute.id,
            );

            if (currentAttribute) {
              currentAttribute.numericValue = object.height;
            } else {
              product.attributeSettings.push(
                new AttributeSettingsDTO(Number(heightAttribute.id), undefined, object.height),
              );
            }
          }

          if (widthAttribute) {
            const currentAttribute = product.attributeSettings.find(
              (attr) => attr.key.toString() === widthAttribute.id,
            );

            if (currentAttribute) {
              currentAttribute.numericValue = object.width;
            } else {
              product.attributeSettings.push(
                new AttributeSettingsDTO(Number(widthAttribute.id), undefined, object.width),
              );
            }
          }

          products.push(product);
        }
      }
    }

    return products;
  }

  public removeWallObjects() {
    for (const wall of this._walls) {
      wall.setObjects([]);
    }
  }

  public getWallObjects() {
    const objects: WallObject[] = [];

    for (const wall of this._walls) {
      const wallObjects = wall.getObjects();
      for (const wallObject of wallObjects) {
        objects.push(wallObject);
      }
    }

    return objects;
  }

  /**
   * Creates a new wall.
   * @param start The start corner.
   * @param end he end corner.
   * @returns The new wall.
   */
  public newWall(start: Corner, end: Corner, objects?: WallObject[]): Wall {
    const wall = new Wall(this, start, end);
    this._walls.push(wall);

    wall.fireOnDelete(() => {
      this.removeWall(wall);
    });

    wall.fireOnMove(() => {
      this.fireRedraw();
    });

    if (objects) wall.setObjects(objects);
    //this.new_wall_callbacks.fire(wall);
    this._newWallCallbacks.forEach((callback: Fn) => callback(wall));

    this.update();
    return wall;
  }

  /** Removes a wall.
   * @param wall The wall to be removed.
   */
  private removeWall(wall: Wall) {
    Utils.removeValue(this._walls, wall);
    this.update();
  }

  /**
   * Creates a new corner.
   * @param x The x coordinate.
   * @param y The y coordinate.
   * @param id An optional id. If unspecified, the id will be created internally.
   * @returns The new corner.
   */
  public newCorner(x: number, y: number, z: number, uuid?: string): Corner {
    const corner = new Corner(this, x, y, z, uuid);
    this._corners.push(corner);

    corner.fireOnDelete(() => {
      this.removeCorner(corner);
    });

    //this.new_corner_callbacks.fire(corner);
    this._newCornerCallbacks.forEach((callback: Fn) => callback(corner));
    return corner;
  }

  /** Removes a corner.
   * @param corner The corner to be removed.
   */
  private removeCorner(corner: Corner) {
    Utils.removeValue(this._corners, corner);
  }

  /** Gets the walls. */
  public getWalls(): Wall[] {
    return this._walls;
  }

  /** Gets the corners. */
  public getCorners(): Corner[] {
    return this._corners;
  }

  /** Gets the rooms. */
  public getRooms(): Room[] {
    return this._rooms;
  }

  public hasUtility(): boolean {
    let check = false;

    for (const wall of this._walls) {
      if (wall.hasUtility()) {
        check = true;
        break;
      }
    }

    return check;
  }

  public setUtilities(utilities: Utility[]) {
    for (const wall of this._walls) {
      wall.setUtilities(utilities);
    }
  }

  public overlappedCorner(x: number, y: number, tolerance?: number): Corner | null {
    tolerance = tolerance || this._defaultFloorPlanTolerance;
    for (let i = 0; i < this._corners.length; i++) {
      if (this._corners[i].distanceFrom(x, y) < tolerance) {
        return this._corners[i];
      }
    }
    return null;
  }

  public overlappedWall(x: number, y: number, tolerance?: number): Wall | null {
    tolerance = tolerance || this._defaultFloorPlanTolerance;
    for (let i = 0; i < this._walls.length; i++) {
      if (this._walls[i].distanceFrom(x, y) < tolerance) {
        return this._walls[i];
      }
    }
    return null;
  }

  public overlappedObject(x: number, y: number, tolerance?: number): WallObject | null {
    tolerance = tolerance || this._defaultFloorPlanTolerance;
    for (let i = 0; i < this._walls.length; i++) {
      const objects = this._walls[i].getObjects();
      for (let j = 0; j < objects.length; j++) {
        if (objects[j].distanceFrom(x, y) < tolerance) {
          return objects[j];
        }
      }
    }
    return null;
  }

  public overlappedFloor(x: number, y: number): Room | null {
    for (let i = 0; i < this._rooms.length; i++) {
      if (Utils.pointInPolygon(x, y, this._rooms[i].corners)) {
        return this._rooms[i];
      }
    }

    return null;
  }

  public loadFloorplan(floorplan: FloorplanDTO) {
    this.reset();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const corners: any = {};

    if (floorplan === null || !floorplan.corners || !floorplan.walls) {
      return;
    }

    for (const corner of floorplan.corners) {
      corners[corner.uuid] = this.newCorner(corner.x, corner.y, 0, corner.uuid);
    }

    floorplan.walls.forEach((wall) => {
      const newWall = this.newWall(corners[wall.interiorCorners[0]], corners[wall.interiorCorners[1]]);

      if (wall.utilityIds) {
        newWall.setUtilities(
          Utility.utilities.filter((utility) => {
            return utility.id && wall.utilityIds.includes(utility.id);
          }),
        );
      }

      if (wall.wallCovering) {
        const covering = new CoveringDTO(
          wall.wallCovering.type,
          new TextureDataDTO(
            wall.wallCovering.textureData.textureUrl,
            wall.wallCovering.textureData.bumpMapUrl,
            wall.wallCovering.textureData.glossMapUrl,
            wall.wallCovering.textureData.normalMapUrl,
            wall.wallCovering.textureData.roughness,
            wall.wallCovering.textureData.metalness,
            wall.wallCovering.textureData.color,
            wall.wallCovering.textureData.bumpScale,
            wall.wallCovering.textureData.uvMapScale,
            wall.wallCovering.textureData.normalMapScale,
            wall.wallCovering.textureData.colorInfo,
            wall.wallCovering.textureData.sheen,
            wall.wallCovering.textureData.sheenColor,
            wall.wallCovering.textureData.specularIntensity,
            wall.wallCovering.textureData.specularColor,
            wall.wallCovering.textureData.defaultColorId,
          ),
          wall.wallCovering.thumbnail,
        );

        newWall.frontTexture = covering;
        newWall.backTexture = covering;
      }
    });

    this.update();

    for (let i = 0; i < this._rooms.length; i++) {
      const floor = floorplan.rooms[i].floorCovering;
      const ceiling = floorplan.rooms[i].ceilingCovering;

      this._rooms[i].setFloorTexture(
        new CoveringDTO(
          floor.type,
          new TextureDataDTO(
            floor.textureData.textureUrl,
            floor.textureData.bumpMapUrl,
            floor.textureData.glossMapUrl,
            floor.textureData.normalMapUrl,
            floor.textureData.roughness,
            floor.textureData.metalness,
            floor.textureData.color,
            floor.textureData.bumpScale,
            floor.textureData.uvMapScale,
            floor.textureData.normalMapScale,
            floor.textureData.colorInfo,
            floor.textureData.sheen,
            floor.textureData.sheenColor,
            floor.textureData.specularIntensity,
            floor.textureData.specularColor,
            floor.textureData.defaultColorId,
          ),
          floor.thumbnail,
        ),
      );

      this._rooms[i].setCeilingTexture(
        new CoveringDTO(
          ceiling.type,
          new TextureDataDTO(
            ceiling.textureData.textureUrl,
            ceiling.textureData.bumpMapUrl,
            ceiling.textureData.glossMapUrl,
            ceiling.textureData.normalMapUrl,
            ceiling.textureData.roughness,
            ceiling.textureData.metalness,
            ceiling.textureData.color,
            ceiling.textureData.bumpScale,
            ceiling.textureData.uvMapScale,
            ceiling.textureData.normalMapScale,
            ceiling.textureData.colorInfo,
            ceiling.textureData.sheen,
            ceiling.textureData.sheenColor,
            ceiling.textureData.specularIntensity,
            ceiling.textureData.specularColor,
            ceiling.textureData.defaultColorId,
          ),
          ceiling.thumbnail,
        ),
      );
    }

    //this.roomLoadedCallbacks.fire();
    this.roomLoadedCallbacks.forEach((callback: Fn) => callback());
  }

  /** */
  private reset() {
    const tmpCorners = this._corners.slice(0);
    const tmpWalls = this._walls.slice(0);

    tmpCorners.forEach((corner) => corner.remove());
    tmpWalls.forEach((wall) => wall.remove());

    this._corners = [];
    this._walls = [];
  }

  /**
   * Update rooms
   */
  public update() {
    this._walls.forEach((wall) => {
      wall.resetFrontBack();
    });

    const roomCorners = this.findRooms(this._corners);
    const newRooms = roomCorners.map((corners) => new Room(this, corners));

    this._rooms.forEach((room) => {
      newRooms.forEach((newRoom) => {
        if (room.equals(newRoom)) {
          newRoom.setCeilingTexture(room.getCeilingTexture());
          newRoom.setFloorTexture(room.getFloorTexture());
        }
      });
    });

    this._rooms = newRooms;
    this.assignOrphanEdges();
    this._updatedRooms.forEach((callback: Fn) => callback());
  }

  /**
   * Returns the center of the floorplan in the y plane
   */
  public getCenter() {
    return this.getDimensions(true);
  }

  public getSize() {
    return this.getDimensions(false);
  }

  public getDimensions(center: boolean) {
    center = center || false; // otherwise, get size

    let xMin = Infinity;
    let xMax = -Infinity;
    let zMin = Infinity;
    let zMax = -Infinity;

    this._corners.forEach((corner) => {
      if (corner.x < xMin) xMin = corner.x;
      if (corner.x > xMax) xMax = corner.x;
      if (corner.y < zMin) zMin = corner.y;
      if (corner.y > zMax) zMax = corner.y;
    });

    let ret;

    if (xMin === Infinity || xMax === -Infinity || zMin === Infinity || zMax === -Infinity) {
      ret = new Vector3();
    } else {
      if (center) {
        // center
        ret = new Vector3((xMin + xMax) * 0.5, 0, (zMin + zMax) * 0.5);
      } else {
        // size
        ret = new Vector3(xMax - xMin, 0, zMax - zMin);
      }
    }

    return ret;
  }

  private assignOrphanEdges() {
    // kinda hacky
    // find orphaned wall segments (i.e. not part of rooms) and
    // give them edges
    const orphanWalls = [];

    this._walls.forEach((wall) => {
      if (!wall.backEdge && !wall.frontEdge) {
        wall.orphan = true;
        //const back = new HalfEdge(null, wall, false);
        //back.generatePlane();
        //const front = new HalfEdge(null, wall, true);
        //front.generatePlane();
        orphanWalls.push(wall);
      }
    });
  }

  /*
   * Find the "rooms" in our planar straight-line graph.
   * Rooms are set of the smallest (by area) possible cycles in this graph.
   * @param corners The corners of the floorplan.
   * @returns The rooms, each room as an array of corners.
   */
  public findRooms(corners: Corner[]): Corner[][] {
    function _calculateTheta(previousCorner: Corner, currentCorner: Corner, nextCorner: Corner) {
      return Utils.angle2pi(
        previousCorner.x - currentCorner.x,
        previousCorner.y - currentCorner.y,
        nextCorner.x - currentCorner.x,
        nextCorner.y - currentCorner.y,
      );
    }

    function _removeDuplicateRooms(roomArray: Corner[][]): Corner[][] {
      const results: Corner[][] = [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const lookup: any = {};

      const hashFunc = function (corner: Corner) {
        return corner.uuid;
      };

      const sep = '-';
      for (let i = 0; i < roomArray.length; i++) {
        // rooms are cycles, shift it around to check uniqueness
        let add = true;
        let str = '';
        const room = roomArray[i];

        for (let j = 0; j < room.length; j++) {
          const roomShift = Utils.cycle(room, j);
          str = Utils.map(roomShift, hashFunc).join(sep);
          if (Object.prototype.hasOwnProperty.call(lookup, str)) {
            add = false;
          }
        }
        if (add) {
          results.push(roomArray[i]);
          lookup[str] = true;
        }
      }
      return results;
    }

    function _findTightestCycle(firstCorner: Corner, secondCorner: Corner): Corner[] {
      const stack: {
        corner: Corner;
        previousCorners: Corner[];
      }[] = [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let next: any = {
        corner: secondCorner,
        previousCorners: [firstCorner],
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const visited: any = {};
      visited[firstCorner.uuid] = true;

      while (next) {
        // update previous corners, current corner, and visited corners
        const currentCorner = next.corner;
        visited[currentCorner.uuid] = true;

        // did we make it back to the startCorner?
        if (next.corner === firstCorner && currentCorner !== secondCorner) {
          return next.previousCorners;
        }

        const addToStack: Corner[] = [];
        const adjacentCorners = next.corner.adjacentCorners();

        for (let i = 0; i < adjacentCorners.length; i++) {
          const nextCorner = adjacentCorners[i];

          // is this where we came from?
          // give an exception if its the first corner and we aren't at the second corner
          if (nextCorner.uuid in visited && !(nextCorner === firstCorner && currentCorner !== secondCorner)) {
            continue;
          }

          // nope, throw it on the queue
          addToStack.push(nextCorner);
        }

        const previousCorners = next.previousCorners.slice(0);
        previousCorners.push(currentCorner);

        if (addToStack.length > 1) {
          // visit the ones with smallest theta first
          const previousCorner = next.previousCorners[next.previousCorners.length - 1];
          addToStack.sort(function (a, b) {
            return (
              _calculateTheta(previousCorner, currentCorner, b) - _calculateTheta(previousCorner, currentCorner, a)
            );
          });
        }

        if (addToStack.length > 0) {
          // add to the stack
          addToStack.forEach((corner) => {
            stack.push({
              corner: corner,
              previousCorners: previousCorners,
            });
          });
        }

        // pop off the next one
        next = stack.pop();
      }
      return [];
    }

    // find tightest loops, for each corner, for each adjacent
    // TODO: optimize this, only check corners with > 2 adjacents, or isolated cycles
    const loops: Corner[][] = [];

    corners.forEach((firstCorner) => {
      firstCorner.adjacentCorners().forEach((secondCorner) => {
        loops.push(_findTightestCycle(firstCorner, secondCorner));
      });
    });

    // remove duplicates
    const uniqueLoops = _removeDuplicateRooms(loops);
    //remove CW loops
    const uniqueCCWLoops = Utils.removeIf(uniqueLoops, Utils.isClockwise);

    return uniqueCCWLoops;
  }

  public get allCornersHeight() {
    return this._allCornersHeight;
  }

  public set allCornersHeight(value) {
    this._allCornersHeight = value;
  }
}
